import styles from './PricingDetailModule.module.scss';

import Grid from '@material-ui/core/Grid';
import cx from 'classnames';
import type { ReferencedEntry } from 'contentful';
import React, { FC, Fragment, HTMLAttributes, createElement } from 'react';

import type { PricingDetailModule as PricingDetailModuleType } from 'api/contentful/contentTypes.cloverdotcom';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { GridContainer } from 'components/GridContainer/GridContainer';
import { Icon, IconType } from 'components/Icon/Icon';
import { PricingDetailTable } from 'components/PricingDetailTable/PricingDetailTable';
import { TextV2 } from 'components/TextV2/TextV2';
import { useABSolution } from 'hooks/useABSolution';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { FlatEntry, flattenContentfulData } from 'services/cms/flattenContentful';
import { PRICING_DETAIL_MODULE_TEST_ID } from './PricingDetailModule.const';
import { TextWithPaymentsComparisonLink } from 'components/PaymentsComparisonLink/utils/TextWithPaymentsComparisonLink';

const phoneReplacementValue = '{PHONE}';

interface IPricingDetailModuleProps extends HTMLAttributes<HTMLElement> {
  entry?: FlatEntry<PricingDetailModuleType>;
}

const PricingDetailModule: FC<IPricingDetailModuleProps> = ({ entry, style = {}, className, ...props }) => {
  const { browser } = useBreakpoints();
  const isDesktopOrLarge = browser.is.desktop;

  const {
    backgroundColor,
    backLink,
    contactText = phoneReplacementValue,
    phoneLink,
    subTitle,
    subTitleColor,
    title,
    titleColor,
    heroImage,
    solution: solutionEntry,
    headingTagName = 'h2',
  } = entry || {};

  const solution = useABSolution(solutionEntry);

  if (!entry) return null;

  const hasHeroImage = !!heroImage;

  const backLinkText = backLink?.title;

  const phoneLinkComponent = phoneLink ? (
    <ContentLinkV2 entry={phoneLink} className={styles.pricingDetailModuleLink} />
  ) : null;

  return (
    <section
      className={cx(styles.pricingDetailModule, className)}
      style={{ ...style, backgroundColor: backgroundColor || style.backgroundColor }}
      data-testid={PRICING_DETAIL_MODULE_TEST_ID.main}
      {...props}
    >
      <div
        className={cx(styles.pricingDetailModuleHeroWrapper, {
          [styles.pricingDetailModuleHeroWrapperWithCover]: hasHeroImage,
        })}
        data-testid={PRICING_DETAIL_MODULE_TEST_ID.hero}
        style={{ backgroundImage: hasHeroImage ? `url(${heroImage?.file?.url})` : undefined }}
      >
        <GridContainer justifyContent="space-between" alignItems="center">
          <Grid item>
            {backLinkText && (
              <ContentLinkV2
                entry={backLink}
                hideTitle
                className={cx(styles.pricingDetailModuleLink, styles.pricingDetailModuleBackLink, {
                  [styles.pricingDetailModuleBackLinkWithCover]: hasHeroImage,
                })}
                data-testid={PRICING_DETAIL_MODULE_TEST_ID.back}
                variant="body"
              >
                <Icon type={IconType.CHEVRON_LEFT} />
                <p>{backLinkText}</p>
              </ContentLinkV2>
            )}
          </Grid>
          <Grid item>
            <div className={styles.pricingDetailModuleContact}>
              {isDesktopOrLarge
                ? contactText.split(phoneReplacementValue).map((text, index) => (
                    <Fragment key={`${text}${index}`}>
                      {index ? phoneLinkComponent : ``}
                      {text}
                    </Fragment>
                  ))
                : phoneLinkComponent}
            </div>
          </Grid>
        </GridContainer>
        <GridContainer justifyContent="center">
          <Grid item xs={12} md={9} lg={10}>
            <div className={styles.pricingDetailModuleHero}>
              {title && (
                <TextV2
                  hasId
                  isMarkdown
                  value={title}
                  entry={{ ...entry, field: 'title' }}
                  tagName={headingTagName}
                  variant="h1"
                  className={styles.pricingDetailModuleTitle}
                  style={{ color: titleColor }}
                />
              )}
            </div>
            {subTitle && (
              <div className={styles.pricingDetailModuleSubTitle}>
                <TextWithPaymentsComparisonLink text={subTitle} textColor={subTitleColor} />
              </div>
            )}
          </Grid>
        </GridContainer>
      </div>
      {solution &&
        (isDesktopOrLarge ? (
          <GridContainer
            justifyContent="center"
            className={cx({ [styles.pricingDetailModuleContentWithCover]: hasHeroImage })}
            data-testid={PRICING_DETAIL_MODULE_TEST_ID.content}
          >
            <Grid item xs={12} xl={10}>
              <div className={styles.pricingDetailModuleTableContainerWithoutTab}>
                <PricingDetailTable solution={solution} />
              </div>
            </Grid>
          </GridContainer>
        ) : (
          <PricingDetailTable solution={solution} />
        ))}
    </section>
  );
};

PricingDetailModule.displayName = 'PricingDetailModule';

interface IPricingDetailModuleWrapperProps extends HTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<PricingDetailModuleType>;
}

const PricingDetailModuleWrapper: FC<IPricingDetailModuleWrapperProps> = ({ entry, ...props }) =>
  createElement(PricingDetailModule, { ...props, entry: flattenContentfulData(entry) });

PricingDetailModuleWrapper.displayName = 'PricingDetailModuleWrapper';

export { PricingDetailModule as _PricingDetailModule, PricingDetailModuleWrapper as PricingDetailModule };
